import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CssBaseline, CircularProgress, Box, Typography } from '@mui/material';
import { useAuth } from './context/AuthContext';
import { StripeProvider } from './context/StripeContext';
import { firebaseConfig } from './utils/APIConfig';
import FirebaseService from './services/FirebaseService';
import { getApps, initializeApp } from 'firebase/app';

// Screens
import LoadingScreen from './screens/LoadingScreen';
import FormChatScreen from './screens/FormChatScreen';
import HomeScreen from './screens/HomeScreen';
import ContinuationScreen from './screens/ContinuationScreen';
import BusinessHomeScreen from './screens/BusinessHomeScreen';
import BusinessAboutScreen from './screens/BusinessAboutScreen';
import AppHandoffScreen from './screens/AppHandoffScreen';

// Initialize Firebase immediately if not already initialized
if (!getApps().length) {
  initializeApp(firebaseConfig);
}

// Remount the chat screen when the agent changes (e.g. a chat transfer from
// /form/A to /form/B) so no per-agent state leaks across.
const KeyedFormChat: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  return <FormChatScreen key={formId} />;
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  
  // Configure Firebase on app startup
  useEffect(() => {
    const configureFirebase = async () => {
      try {
        const firebaseService = FirebaseService.getInstance();
        await firebaseService.initialize(firebaseConfig);
        setIsFirebaseReady(true);
      } catch (error) {
        console.error('Failed to configure Firebase:', error);
        // Still set ready to true to allow the app to proceed
        setIsFirebaseReady(true);
      }
    };
    
    configureFirebase();
  }, []);

  if (!isFirebaseReady) {
    return (
      <>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: 'background.default',
        }}>
          <CircularProgress size={52} thickness={3.5} sx={{ mb: 4, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ mb: 1, color: 'primary.main' }}>
            Paiper
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Getting things ready…
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <CssBaseline />
      <StripeProvider>
        <Router>
          <Routes>
            <Route path="/" element={<HomeScreen />} />
            <Route path="/loading" element={<LoadingScreen />} />
            <Route path="/form/:formId" element={<KeyedFormChat />} />
            <Route path="/b/:channelId" element={<BusinessHomeScreen />} />
            <Route path="/b/:channelId/about" element={<BusinessAboutScreen />} />
            <Route path="/a/:code" element={<AppHandoffScreen />} />
            <Route path="/c/:token" element={<ContinuationScreen />} />
            <Route path="*" element={<HomeScreen />} />
          </Routes>
        </Router>
      </StripeProvider>
    </>
  );
};

export default App;
